import { useEffect, useState } from "react";
import EmployerHeader from "../components/employer/EmployerHeader";
import EmployerTopNav from "../components/employer/EmployerTopNav";

/**
 * Intellerview — Simulation Library
 * Self-contained, drop-in page component. Scoped styles (no Tailwind dependency).
 *
 * INTEGRATION POINTS (the only things you should need to change):
 *   1. createLinkHref()  -> point at your real "create candidate link" route
 *   2. requestBuildHref() -> point at your real "request build" route
 *   3. CONFIG             -> the category/simulation data (ideally fed from your DB later)
 *   4. --lib-top in STYLES -> adjust if your global nav height differs from 64px
 *
 * Do not restyle, rename classes, or convert to Tailwind. The design is locked.
 */

type SimStatus = "available" | "development";

interface Simulation {
  name: string;
  simulationId?: string;
  level?: string;
  status: SimStatus;
  desc: string;
}

interface Category {
  id: string;
  name: string;
  desc: string;
  sims: Simulation[];
}

/* >>> WIRE THESE TWO TO YOUR REAL ROUTES <<< */
const createLinkHref = (sim: Simulation) =>
  sim.simulationId
    ? `/sim/create-link?simulation=${encodeURIComponent(sim.simulationId)}`
    : `/sim/create-link?role=${encodeURIComponent(sim.name)}`;
const requestBuildHref = (cat: Category) => `/employer/build-request?category=${cat.id}`;

const CONFIG: Category[] = [
  {
    id: "banking",
    name: "Banking & Finance",
    desc: "Deal execution, financial analysis, credit and risk roles across corporate and investment banking.",
    sims: [
      {
        name: "M&A / Corporate Finance Analyst",
        simulationId: "INVESTMENT-BANKER",
        level: "Analyst / VP",
        status: "available",
        desc: "Deal structuring, financial modelling, credit analysis, policy application and commercial judgement in a realistic mining-sector M&A scenario.",
      },
      {
        name: "Credit Analyst",
        level: "Analyst",
        status: "development",
        desc: "Counterparty risk assessment, covenant analysis and lending judgement against a structured credit file.",
      },
      {
        name: "Equity Research Associate",
        level: "Associate",
        status: "development",
        desc: "Company analysis, valuation build and investment thesis under a sector coverage brief.",
      },
    ],
  },
  {
    id: "founders",
    name: "Founders & New Ventures",
    desc: "The first critical hires that make or break a young company, assessed for real capability before you commit limited runway.",
    sims: [
      {
        name: "First Commercial Hire",
        status: "development",
        desc: "Building pipeline, closing early customers and representing a young brand without an established playbook.",
      },
      {
        name: "Founding Operator",
        status: "development",
        desc: "Standing up finance, process and people systems from zero in a new venture.",
      },
    ],
  },
  {
    id: "governance",
    name: "Leadership & Governance",
    desc: "Board, executive and oversight roles, assessed on fiduciary judgement, financial literacy and strategic governance.",
    sims: [
      {
        name: "Board Member / Non-Executive Director",
        level: "Board",
        status: "development",
        desc: "Fiduciary judgement, financial literacy, strategic oversight and risk governance, assessed against realistic board papers.",
      },
      {
        name: "Executive Leadership",
        level: "C-suite",
        status: "development",
        desc: "Strategic decision-making, capital allocation and stakeholder judgement under executive-level scenarios.",
      },
    ],
  },
  {
    id: "legal",
    name: "Legal & Compliance",
    desc: "Drafting, regulatory analysis, contract review and compliance judgement for legal and risk functions.",
    sims: [],
  },
  {
    id: "tech",
    name: "Technology & Engineering",
    desc: "Software, data and systems roles tested on practical build, debug and design tasks.",
    sims: [],
  },
  {
    id: "sales",
    name: "Sales & Commercial",
    desc: "Pipeline, negotiation and commercial reasoning for revenue-generating roles.",
    sims: [],
  },
  {
    id: "ops",
    name: "Operations & Supply Chain",
    desc: "Planning, logistics and process roles tested on real operational decisions.",
    sims: [],
  },
  {
    id: "marketing",
    name: "Marketing & Communications",
    desc: "Strategy, campaign and content roles assessed on applied commercial judgement.",
    sims: [],
  },
  {
    id: "cs",
    name: "Customer Service & Contact Centre",
    desc: "High-volume frontline roles across contact centres and customer support, assessed on problem resolution, systems navigation and communication accuracy.",
    sims: [
      {
        name: "Customer Service Agent",
        status: "development",
        desc: "Query resolution, systems navigation, policy application and clear written and verbal communication under realistic call and ticket scenarios.",
      },
      {
        name: "Contact Centre Team Leader",
        status: "development",
        desc: "Queue management, coaching judgement, escalation handling and performance decisions across a simulated team.",
      },
    ],
  },
];

// Index label is rendered as `0${i+1}`. Correct for 1-9 categories.
// If the library ever grows past 9, switch to padStart: String(i + 1).padStart(2, "0").
const indexLabel = (i: number) => `0${i + 1}`;

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

.ilv-lib{
  --teal:#0d9488;--teal-dark:#0f766e;--teal-light:#ccfbf1;--teal-pale:#f0fdfa;
  --ink:#0f1a1a;--ink-mid:#2d4444;--ink-soft:#5a7070;--ink-faint:#8aa0a0;
  --rule:#e2ecec;--surface:#f8fafa;--white:#ffffff;--card:#13201f;
  --lib-top:32px;
  font-family:'DM Sans',sans-serif;color:var(--ink);background:var(--white);
  -webkit-font-smoothing:antialiased;
}
.ilv-lib *,.ilv-lib *::before,.ilv-lib *::after{box-sizing:border-box;margin:0;padding:0;}
.ilv-container{max-width:1040px;margin:0 auto;padding:0 2rem;}

.ilv-head{padding:var(--lib-top) 2rem 18px;text-align:center;background:var(--white);}
.ilv-label{font-family:'JetBrains Mono',monospace;font-size:0.68rem;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-faint);margin-bottom:0.75rem;display:block;}
.ilv-h1{font-family:'Playfair Display',serif;font-weight:700;font-size:clamp(1.875rem,3.6vw,2.5rem);line-height:1.14;letter-spacing:-0.025em;color:var(--ink);margin-bottom:0.75rem;}
.ilv-h1 em{font-style:italic;color:var(--teal);}
.ilv-lead{font-size:1rem;line-height:1.65;color:var(--ink-soft);font-weight:300;max-width:540px;margin:0 auto;}

.ilv-grid-wrap{padding:2rem 2rem 72px;background:var(--ink);}
.ilv-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;}
@media(max-width:820px){.ilv-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:520px){.ilv-grid{grid-template-columns:1fr;}}

.ilv-card{position:relative;overflow:hidden;border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:1.75rem;background:var(--card);cursor:pointer;transition:border-color 0.18s,transform 0.18s,box-shadow 0.18s;display:flex;flex-direction:column;min-height:208px;}
.ilv-card:hover{border-color:var(--teal);transform:translateY(-3px);box-shadow:0 14px 36px -16px rgba(13,148,136,0.5);}
.ilv-wm{position:absolute;top:-0.4em;right:-0.02em;font-family:'Playfair Display',serif;font-weight:700;font-size:7rem;line-height:1;color:rgba(94,234,212,0.07);z-index:0;pointer-events:none;}
.ilv-card:hover .ilv-wm{color:rgba(94,234,212,0.12);}
.ilv-mid{flex:1;display:flex;align-items:center;position:relative;z-index:1;}
.ilv-name{font-family:'Playfair Display',serif;font-weight:700;font-size:1.4375rem;line-height:1.2;color:var(--white);}
.ilv-foot{position:relative;z-index:1;display:flex;align-items:center;justify-content:space-between;margin-top:1.5rem;padding-top:1.1rem;border-top:1px solid rgba(255,255,255,0.1);}
.ilv-count{font-family:'JetBrains Mono',monospace;font-size:0.66rem;letter-spacing:0.08em;text-transform:uppercase;color:rgba(255,255,255,0.4);}
.ilv-count.has{color:#5eead4;}
.ilv-view{font-size:0.8125rem;color:#5eead4;font-weight:500;}

.ilv-scrim{position:fixed;inset:0;background:rgba(15,26,26,0.45);opacity:0;pointer-events:none;transition:opacity 0.25s;z-index:200;}
.ilv-scrim.show{opacity:1;pointer-events:auto;}
.ilv-drawer{position:fixed;top:0;right:0;bottom:0;width:min(480px,92vw);background:var(--white);z-index:210;transform:translateX(100%);transition:transform 0.3s cubic-bezier(.4,0,.2,1);display:flex;flex-direction:column;box-shadow:-20px 0 60px -20px rgba(0,0,0,0.3);}
.ilv-drawer.show{transform:translateX(0);}
.ilv-drawer-head{padding:1.75rem 1.75rem 1.5rem;border-bottom:1px solid var(--rule);background:var(--surface);position:relative;}
.ilv-drawer-close{position:absolute;top:1.25rem;right:1.25rem;width:30px;height:30px;border:1px solid var(--rule);border-radius:4px;background:var(--white);cursor:pointer;color:var(--ink-soft);font-size:1rem;line-height:1;}
.ilv-drawer-close:hover{border-color:var(--teal);color:var(--teal);}
.ilv-drawer-label{font-family:'JetBrains Mono',monospace;font-size:0.65rem;letter-spacing:0.12em;text-transform:uppercase;color:var(--teal);margin-bottom:0.5rem;}
.ilv-drawer-title{font-family:'Playfair Display',serif;font-weight:700;font-size:1.5rem;color:var(--ink);margin-bottom:0.5rem;}
.ilv-drawer-sub{font-size:0.875rem;line-height:1.6;color:var(--ink-soft);font-weight:300;}
.ilv-drawer-body{padding:1.5rem 1.75rem;overflow-y:auto;flex:1;}

.ilv-sim{border:1px solid var(--rule);border-radius:6px;padding:1.25rem 1.4rem;margin-bottom:1rem;}
.ilv-sim-name{font-family:'Playfair Display',serif;font-weight:700;font-size:1.0625rem;color:var(--ink);line-height:1.3;}
.ilv-sim-meta{display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;margin:0.55rem 0 0.85rem;}
.ilv-tag{font-family:'JetBrains Mono',monospace;font-size:0.58rem;letter-spacing:0.08em;text-transform:uppercase;padding:0.22rem 0.55rem;border-radius:2px;font-weight:500;}
.ilv-tag.lvl{background:var(--surface);border:1px solid var(--rule);color:var(--ink-soft);}
.ilv-tag.live{background:var(--teal-light);border:1px solid rgba(13,148,136,0.3);color:var(--teal-dark);}
.ilv-tag.dev{background:#fef3c7;border:1px solid #d97706;color:#92400e;}
.ilv-sim-desc{font-size:0.875rem;line-height:1.55;color:var(--ink-soft);font-weight:300;margin-bottom:1rem;}

.ilv-btn{font-family:'DM Sans',sans-serif;font-size:0.8125rem;font-weight:500;padding:0.625rem 1rem;border-radius:4px;text-decoration:none;cursor:pointer;border:none;display:inline-block;text-align:center;}
.ilv-btn.full{display:block;width:100%;}
.ilv-btn.solid{background:var(--teal);color:#fff;}
.ilv-btn.solid:hover{background:var(--teal-dark);}
.ilv-btn.disabled{background:var(--surface);color:var(--ink-faint);border:1px solid var(--rule);cursor:not-allowed;}
.ilv-btn.outline{background:transparent;color:var(--teal);border:1px solid var(--teal);}
.ilv-btn.outline:hover{background:var(--teal-pale);}

.ilv-empty{text-align:center;padding:2rem 1rem;border:1px dashed var(--rule);border-radius:6px;margin-bottom:1.5rem;}
.ilv-empty p{font-size:0.9rem;color:var(--ink-soft);font-weight:300;line-height:1.6;margin-bottom:0.4rem;}
.ilv-empty strong{color:var(--ink);font-weight:500;}
.ilv-req{background:var(--teal-pale);border:1px solid rgba(13,148,136,0.25);border-radius:6px;padding:1.25rem;text-align:center;}
.ilv-req p{font-size:0.875rem;color:var(--ink-mid);font-weight:300;margin-bottom:0.9rem;line-height:1.6;}
.ilv-req strong{color:var(--ink);font-weight:500;}
`;

export default function SimulationLibrary() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const active = openIndex !== null ? CONFIG[openIndex] : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const availableCount = (c: Category) =>
    c.sims.filter((s) => s.status === "available").length;

  return (
    <>
      <EmployerHeader />
      <div className="bg-[#f8faf9] px-4 py-6 sm:px-6 sm:py-8">
        <div className="mx-auto w-full max-w-[1180px]">
          <EmployerTopNav activeSection="simulation-library" />
        </div>
      </div>
      <div className="ilv-lib">
        <style>{STYLES}</style>

        <section className="ilv-head">
          <div className="ilv-container">
            <h1 className="ilv-h1">
              Simulation Library
              <br />
              <em>Everything in one place.</em>
            </h1>
            <p className="ilv-lead">
              Open a sector to see its simulations. Choose a role to create a link
              for your candidate, or request a bespoke build.
            </p>
          </div>
        </section>

        <section className="ilv-grid-wrap">
          <div className="ilv-container">
            <div className="ilv-grid">
              {CONFIG.map((c, i) => {
                const n = availableCount(c);
                const count = n > 0 ? `${n} available` : "Build to order";
                return (
                  <div
                    className="ilv-card"
                    key={c.id}
                    onClick={() => setOpenIndex(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") setOpenIndex(i);
                    }}
                  >
                    <div className="ilv-wm">{indexLabel(i)}</div>
                    <div className="ilv-mid">
                      <div className="ilv-name">{c.name}</div>
                    </div>
                    <div className="ilv-foot">
                      <span className={`ilv-count${n > 0 ? " has" : ""}`}>
                        {count}
                      </span>
                      <span className="ilv-view">View &rarr;</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div
          className={`ilv-scrim${active ? " show" : ""}`}
          onClick={() => setOpenIndex(null)}
        />

        <aside className={`ilv-drawer${active ? " show" : ""}`}>
          {active && openIndex !== null && (
            <>
              <div className="ilv-drawer-head">
                <button
                  className="ilv-drawer-close"
                  onClick={() => setOpenIndex(null)}
                  aria-label="Close"
                >
                  &#10005;
                </button>
                <div className="ilv-drawer-label">
                  {`Category ${indexLabel(openIndex)}`}
                </div>
                <div className="ilv-drawer-title">{active.name}</div>
                <div className="ilv-drawer-sub">{active.desc}</div>
              </div>

              <div className="ilv-drawer-body">
                {active.sims.length > 0 ? (
                  <>
                    {active.sims.map((s) => {
                      const live = s.status === "available";
                      return (
                        <div className="ilv-sim" key={s.name}>
                          <div className="ilv-sim-name">{s.name}</div>
                          <div className="ilv-sim-meta">
                            <span className={`ilv-tag ${live ? "live" : "dev"}`}>
                              {live ? "Available now" : "In development"}
                            </span>
                            {s.level ? (
                              <span className="ilv-tag lvl">{s.level}</span>
                            ) : null}
                          </div>
                          <p className="ilv-sim-desc">{s.desc}</p>
                          {live ? (
                            <a className="ilv-btn solid full" href={createLinkHref(s)}>
                              Create link for candidate
                            </a>
                          ) : (
                            <span className="ilv-btn disabled full">
                              In development
                            </span>
                          )}
                        </div>
                      );
                    })}
                    <div className="ilv-req">
                      <p>
                        <strong>Need a different role in this sector?</strong> We
                        build bespoke simulations to order.
                      </p>
                      <a
                        className="ilv-btn outline full"
                        href={requestBuildHref(active)}
                      >
                        Request simulation build
                      </a>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="ilv-empty">
                      <p>
                        <strong>No simulations published here yet.</strong>
                      </p>
                      <p>
                        We can build one for your exact role, mapped to recognised
                        hard-skill standards.
                      </p>
                    </div>
                    <div className="ilv-req">
                      <p>
                        <strong>Get this category started.</strong> Tell us the
                        role and we build it to order.
                      </p>
                      <a
                        className="ilv-btn solid full"
                        href={requestBuildHref(active)}
                      >
                        Request simulation build
                      </a>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  );
}
